import { CloudUploadOutlined, FilePdfOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Select,
  Tabs,
  Upload,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import PageTitle from '../../../components/PageTitle'
import { DATE_FORMAT_LIST } from '../../../constants'
import { Container } from '../../../layouts/Container/Container'
import DashboardLayout from '../../../layouts/DashboardLayout'
import validator from '../../../validator'
import {
  FormActionFooter,
  ViewDetailContent,
} from './BoardResolutionMinutesUpdateStyled'
import { ArrowLeftBroken } from '../../../components/AppIcon'
import DraggerUploadButton from '../../../components/Common/DraggerUploadButton'
import AppPageHeader from '../../../components/AppPageHeader'

const BoardResolutionMinutesUpdate = props => {
  const { Option } = Select
  const {
    loadingAnimationStore,
    boardResolutionMinutesStore,
    match,
    fileStore,
    history,
  } = props

  const [form] = Form.useForm()

  const [fileList, setFileList] = useState([]) // Biên bản
  const [fileListNQ, setFileListNQ] = useState([]) // Nghị quyết
  const [originalData, setOriginalData] = useState({})

  const { documentId } = match.params

  const handleAddFileToUpload = () => false

  const renderFileItem = (originNode, file, fileList, actions) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 0',
      }}>
      <FilePdfOutlined
        style={{ color: '#ff4d4f', fontSize: 14, flexShrink: 0 }}
      />
      <span
        style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
        {file.name}
      </span>
      <span
        onClick={actions.remove}
        style={{
          cursor: 'pointer',
          color: '#8c8c8c',
          fontSize: 12,
          flexShrink: 0,
        }}>
        ✕
      </span>
    </div>
  )

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  }

  const handleChangeFile = useCallback(
    info => {
      form.setFieldsValue({ attachment_minutes: info.fileList })
      setFileList(info.fileList)
    },
    [form]
  )

  const handleChangeFileNQ = useCallback(
    info => {
      form.setFieldsValue({ attachment_solution: info.fileList })
      setFileListNQ(info.fileList)
    },
    [form]
  )

  useEffect(() => {
    loadingAnimationStore.showSpinner(true)
    boardResolutionMinutesStore
      .getBoardResolutionMinutesDetail(documentId)
      .then(
        res => {
          loadingAnimationStore.showSpinner(false)

          const {
            document_name,
            document_number,
            contruction_board,
            issue_date,
            status,
            issue_level,
            attachments = [],
            attachments_minutes = [],
            attachments_resolution = [],
          } = res.data

          // Map file ID → object { uid, id, name, status } để Ant Design Upload nhận diện
          const toFileItem = (fileId, index, prefix) => {
            const matched = attachments.find(a => a.id === fileId)
            return {
              uid: `${prefix}-${index}`,
              id: fileId,
              name: matched?.name || fileId,
              status: 'done',
            }
          }

          const minutesFiles = attachments_minutes.map((id, i) =>
            toFileItem(id, i, 'minutes')
          )
          const solutionFiles = attachments_resolution.map((id, i) =>
            toFileItem(id, i, 'solution')
          )

          form.setFieldsValue({
            document_name: document_name?.trimStart(),
            document_number: document_number?.trimStart(),
            contruction_board: contruction_board?.trimStart(),
            issue_date: moment(issue_date),
            status: status === 'NEW' ? undefined : status,
            issue_level: issue_level?.trimStart(),
            attachment_minutes: minutesFiles,
            attachment_solution: solutionFiles,
          })

          setFileList(minutesFiles)
          setFileListNQ(solutionFiles)

          setOriginalData({
            document_name: document_name?.trimStart() || null,
            document_number: document_number?.trimStart() || null,
            contruction_board: contruction_board?.trimStart() || null,
            issue_level: issue_level?.trimStart() || null,
            status: status === 'NEW' ? null : status || null,
            issue_date,
            attachments_minutes,
            attachments_resolution,
          })
        },
        error => {
          message.error(error.vi)
          loadingAnimationStore.showSpinner(false)
        }
      )
    return () => boardResolutionMinutesStore.clearBoardHDQTDetail()
  }, [boardResolutionMinutesStore, documentId, form, loadingAnimationStore])

  const compareData = (originalData, currentValues) => {
    const fieldsToCompare = [
      'document_name',
      'document_number',
      'contruction_board',
      'issue_level',
      'status',
    ]

    for (const field of fieldsToCompare) {
      const originalValue = originalData[field] || null
      const currentValue = currentValues[field] || null
      if (originalValue !== currentValue) return false
    }

    if (currentValues.issue_date && moment.isMoment(currentValues.issue_date)) {
      const currentDateStr = currentValues.issue_date
        .hour(12)
        .minute(0)
        .second(0)
        .toISOString()
      const originalDateStr = moment(originalData.issue_date)
        .hour(12)
        .minute(0)
        .second(0)
        .toISOString()
      if (originalDateStr !== currentDateStr) return false
    }

    // Kiểm tra có file mới được chọn không
    const hasNewMinutes =
      Array.isArray(currentValues.attachment_minutes) &&
      currentValues.attachment_minutes.some(f => f.originFileObj)
    const hasNewSolution =
      Array.isArray(currentValues.attachment_solution) &&
      currentValues.attachment_solution.some(f => f.originFileObj)

    if (hasNewMinutes || hasNewSolution) return false

    // Kiểm tra số lượng file có thay đổi không (xoá bớt file cũ)
    const currentMinuteIds = (currentValues.attachment_minutes || [])
      .map(f => f.id)
      .filter(Boolean)
    const currentSolutionIds = (currentValues.attachment_solution || [])
      .map(f => f.id)
      .filter(Boolean)

    if (
      currentMinuteIds.length !==
        (originalData.attachments_minutes || []).length ||
      currentSolutionIds.length !==
        (originalData.attachments_resolution || []).length
    )
      return false

    return true
  }

  const handleSubmitForm = async values => {
    if (compareData(originalData, values)) {
      message.warning('Không có giá trị thay đổi')
      return
    }

    loadingAnimationStore.showSpinner(true)

    try {
      const submitData = {
        ...values,
        status: values.status || null,
      }

      if (submitData.issue_date && moment.isMoment(submitData.issue_date)) {
        submitData.issue_date = submitData.issue_date
          .hour(12)
          .minute(0)
          .second(0)
          .toISOString()
      }

      // Upload Biên bản
      let fileIds = []
      if (Array.isArray(values.attachment_minutes)) {
        for (let file of values.attachment_minutes) {
          if (file.originFileObj) {
            const formData = new FormData()
            formData.append('file', file.originFileObj)
            const res = await fileStore.uploadFile(formData)
            fileIds.push(res.data.file_id)
          } else if (file.id) {
            fileIds.push(file.id)
          }
        }
      }

      // Upload Nghị quyết
      let fileIdsNQ = []
      if (Array.isArray(values.attachment_solution)) {
        for (let file of values.attachment_solution) {
          if (file.originFileObj) {
            const formDataNQ = new FormData()
            formDataNQ.append('file', file.originFileObj)
            const resNQ = await fileStore.uploadFile(formDataNQ)
            fileIdsNQ.push(resNQ.data.file_id)
          } else if (file.id) {
            fileIdsNQ.push(file.id)
          }
        }
      }

      submitData.attachments_minutes = fileIds
      submitData.attachments_resolution = fileIdsNQ
      delete submitData.attachment_minutes
      delete submitData.attachment_solution

      handleUpdateBoardHDQT(documentId, submitData)
    } catch (error) {
      message.error(error.vi)
      loadingAnimationStore.showSpinner(false)
    }
  }

  const handleUpdateBoardHDQT = useCallback(
    (documentId, submitData) => {
      boardResolutionMinutesStore
        .updateBoardResolutionMinutes(documentId, submitData)
        .then(() => {
          loadingAnimationStore.showSpinner(false)
          message.success('Cập nhật biên bản nghị quyết thành công')
          history.push(`/board-resolution-minutes-hdqt/view/${documentId}`)
        })
        .catch(error => {
          loadingAnimationStore.showSpinner(false)
          message.error(error.vi)
        })
    },
    [boardResolutionMinutesStore, history, loadingAnimationStore]
  )

  const handleCancel = () => {
    form.resetFields()
    history.push(`/board-resolution-minutes-hdqt/view/${documentId}`)
  }

  const options = [
    { label: 'Đã bãi bỏ', value: 'ABOLISHED' },
    { label: 'Hết hiệu lực', value: 'EXPIRED' },
    { label: 'Còn hiệu lực', value: 'ACTIVE' },
  ]

  const renderFormContent = () => (
    <Form
      scrollToFirstError={true}
      layout="vertical"
      form={form}
      name={'add-vbpc-form'}
      onFinish={handleSubmitForm}>
      <div className="custom-create-page-content-wrapper">
        <Form.Item
          name={'document_name'}
          label={'Tên văn bản'}
          rules={[
            { required: true, message: 'Vui lòng nhập tên văn bản' },
            { validator: validator.validateInputString },
          ]}>
          <Input placeholder={'Nhập tên văn bản'} />
        </Form.Item>

        <Form.Item
          name={'document_number'}
          label={'Số quyết định ban hành'}
          rules={[
            {
              required: true,
              message: 'Vui lòng nhập số quyết định ban hành',
            },
            { validator: validator.validateInputString },
          ]}>
          <Input placeholder={'Nhập số quyết định ban hành'} />
        </Form.Item>

        <Form.Item
          name={'issue_date'}
          label={'Ngày ban hành'}
          rules={[{ required: true, message: 'Vui lòng chọn ngày ban hành' }]}>
          <DatePicker style={{ width: '100%' }} format={DATE_FORMAT_LIST} />
        </Form.Item>

        {/* <Form.Item name={'issue_level'} label={'Cấp ban hành'}>
            <Input placeholder={'Nhập cấp ban hành'} />
          </Form.Item> */}

        <Form.Item name={'contruction_board'} label={'Ban chủ trì xây dựng'}>
          <Input placeholder={'Nhập ban chủ trì xây dựng'} />
        </Form.Item>

        {/* <Form.Item name={'status'} label={'Trạng thái'}>
            <Select
              showSearch
              allowClear
              placeholder={'Trạng thái'}
              filterOption={true}
              optionFilterProp={'label'}>
              {options.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item> */}

        <Tabs
          defaultActiveKey="1"
          destroyInactiveTabPane={false}
          style={{
            marginTop: 20,
          }}>
          <Tabs.TabPane tab="Biên bản" key="1" forceRender>
            {/* Biên bản */}
            <Form.Item
              name={'attachment_minutes'}
              label={'Biên bản'}
              rules={[{ required: true, message: 'Vui lòng thêm tài liệu' }]}>
              <Upload.Dragger
                fileList={fileList}
                onChange={handleChangeFile}
                beforeUpload={handleAddFileToUpload}
                multiple={true}
                itemRender={renderFileItem}
                className="custom-dragger-upload bg-white">
                <DraggerUploadButton />
              </Upload.Dragger>
            </Form.Item>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Nghị quyết" key="2" forceRender>
            {/* Nghị quyết */}
            <Form.Item
              name={'attachment_solution'}
              label={'Nghị quyết'}
              rules={[{ required: true, message: 'Vui lòng thêm tài liệu' }]}>
              <Upload.Dragger
                fileList={fileListNQ}
                onChange={handleChangeFileNQ}
                beforeUpload={handleAddFileToUpload}
                multiple={true}
                itemRender={renderFileItem}
                className="custom-dragger-upload bg-white">
                <DraggerUploadButton />
              </Upload.Dragger>
            </Form.Item>
          </Tabs.TabPane>
        </Tabs>
      </div>

      <div className="custom-create-page-footer-sticky">
        <Button
          danger
          className="custom-ant-button w-160"
          htmlType={'button'}
          onClick={handleCancel}>
          Huỷ bỏ
        </Button>

        <Button
          type={'primary'}
          className="custom-ant-button w-160"
          htmlType={'submit'}>
          Cập nhật biên bản
        </Button>
      </div>
    </Form>
  )

  return (
    <DashboardLayout>
      <Helmet>
        <title>Chi tiết biên bản nghị quyết</title>
      </Helmet>

      <AppPageHeader
        title="Cập nhật biên bản nghị quyết"
        showBackButton
      />

      {renderFormContent()}
    </DashboardLayout>
  )
}

BoardResolutionMinutesUpdate.propTypes = {}

export default memo(
  inject(
    'loadingAnimationStore',
    'commonStore',
    'fileStore',
    'boardResolutionMinutesStore'
  )(observer(BoardResolutionMinutesUpdate))
)
